import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLazyGetVehicleQuery } from 'src/redux/api/inventory';
import {
  BatchSelectionParams,
  IMAGE_PROCESSING_BATCH_ITEM_STATUS,
  ImageProcessingBatchItem,
  useApplyImageProcessingBatchItemsMutation,
  useDiscardImageProcessingBatchItemsMutation,
} from 'src/redux/api/inventory/imageProcessingBatches';
import BeforeAfterSlider from 'src/screens/shared/components/BeforeAfterSlider';
import { FormLabel } from 'src/screens/shared/components/Form/FormStyles';
import { StyledModal } from 'src/screens/shared/components/NotificationModal/Styles';
import WandIcon from 'src/screens/shared/components/WandIcon';
import { HeaderModalCustom } from 'src/screens/shared/Styles';
import useResponsive from 'src/utils/hooks/useResponsive';
import { notifyError } from 'src/utils/notificationHelpers';
import styled from 'styled-components';

import { useBulkEnhancement } from './BulkEnhancementContext';
import { getReadyBatchItems, getVehicleDisplayInfo } from './helpers';
import useBatchResultPreview from './useBatchResultPreview';

const ComparisonContainer = styled.div`
  display: flex;
  gap: 16px;

  .comparison-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .image-panel {
    box-sizing: border-box;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 4px;
    border: 1px solid var(--neutral-4);
    overflow: hidden;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 8px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.45);
  }
`;

const ReviewHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const VehicleThumb = styled.div<{ $url?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 4px;
  background-color: var(--secondary-5);
  background-image: ${({ $url }) => ($url ? `url("${$url}")` : 'none')};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const ReviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;

  h4 {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
  }

  span {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.65);
  }
`;

const ReviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
  margin-top: 16px;
`;

const ReviewNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavCounter = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  min-width: 56px;
  text-align: center;
`;

const MediaColumnHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;

  ${FormLabel} {
    margin-bottom: 0;
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }
`;

const MobileItemActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;
`;

const MobilePreviewPanel = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 4px;
  border: 1px solid var(--neutral-4);
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: var(--secondary-5);

  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(0, 0, 0, 0.28);
    color: #fff;
    text-align: center;
  }
`;

type ReviewAction = 'apply-item' | 'discard-item' | 'apply-all' | 'discard-all';

type ItemActionsProps = {
  isBusy: boolean;
  runningAction: ReviewAction | null;
  onApply: () => void;
  onDiscard: () => void;
  size?: 'small' | 'middle' | 'large';
};

const ItemActions = ({ isBusy, runningAction, onApply, onDiscard, size = 'small' }: ItemActionsProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Button
        loading={runningAction === 'discard-item'}
        disabled={isBusy}
        onClick={onDiscard}
        size={size}
      >
        {t('buttons:discard')}
      </Button>
      <Button type="primary" disabled={isBusy} onClick={onApply} size={size}>
        {t('buttons:apply')}
      </Button>
    </>
  );
};

type ReviewItemProps = {
  vehicleId: number;
  item: ImageProcessingBatchItem;
  originalUrl?: string;
  currentIndex: number;
  total: number;
  runningAction: ReviewAction | null;
  onPrevious: () => void;
  onNext: () => void;
  onApply: () => void;
  onDiscard: () => void;
};

const ReviewItemPanel = ({
  vehicleId,
  item,
  originalUrl,
  currentIndex,
  total,
  runningAction,
  onPrevious,
  onNext,
  onApply,
  onDiscard,
}: ReviewItemProps) => {
  const { t } = useTranslation();
  const { isMobileOrTablet } = useResponsive();
  const { previewUrl, isLoading, hasFailed } = useBatchResultPreview({ vehicleId, item });
  const isBusy = runningAction !== null;
  const itemActions = (
    <ItemActions
      isBusy={isBusy}
      runningAction={runningAction}
      onApply={onApply}
      onDiscard={onDiscard}
    />
  );

  const renderMobilePreview = () => {
    if (previewUrl && originalUrl) {
      return (
        <BeforeAfterSlider
          beforeSrc={originalUrl}
          afterSrc={previewUrl}
          beforeLabel={t('vehicleScreen:enhance_image_before')}
          afterLabel={t('vehicleScreen:enhance_image_after')}
        />
      );
    }

    return (
      <MobilePreviewPanel
        style={
          originalUrl || previewUrl
            ? { backgroundImage: `url("${originalUrl || previewUrl}")` }
            : undefined
        }
      >
        {isLoading || hasFailed ? (
          <div className="overlay">
            {isLoading ? <Spin size="large" /> : t('vehicleScreen:enhance_image_result_error')}
          </div>
        ) : null}
      </MobilePreviewPanel>
    );
  };

  return (
    <>
      {isMobileOrTablet ? (
        <>
          <MobileItemActions>{itemActions}</MobileItemActions>
          {renderMobilePreview()}
        </>
      ) : (
        <ComparisonContainer>
          <div className="comparison-column">
            <MediaColumnHeader>
              <FormLabel as="span">{t('vehicleScreen:enhance_image_original_media')}</FormLabel>
            </MediaColumnHeader>
            <div
              className="image-panel"
              style={originalUrl ? { backgroundImage: `url("${originalUrl}")` } : undefined}
            />
          </div>
          <div className="comparison-column">
            <MediaColumnHeader>
              <FormLabel as="span">{t('vehicleScreen:enhance_image_enhanced_media')}</FormLabel>
              <div className="header-actions">{itemActions}</div>
            </MediaColumnHeader>
            <div
              className="image-panel"
              style={previewUrl ? { backgroundImage: `url("${previewUrl}")` } : undefined}
            >
              {isLoading ? <Spin /> : null}
              {hasFailed ? t('vehicleScreen:enhance_image_result_error') : null}
            </div>
          </div>
        </ComparisonContainer>
      )}

      <ReviewToolbar>
        <ReviewNavigation>
          <Button
            type="text"
            size="small"
            icon={<LeftOutlined />}
            disabled={currentIndex === 0}
            onClick={onPrevious}
            aria-label={t('buttons:back')}
          />
          <NavCounter>
            {t('vehicleScreen:bulk_enhance_review_progress', {
              current: currentIndex + 1,
              total,
            })}
          </NavCounter>
          <Button
            type="text"
            size="small"
            icon={<RightOutlined />}
            disabled={currentIndex >= total - 1}
            onClick={onNext}
            aria-label={t('buttons:next')}
          />
        </ReviewNavigation>
      </ReviewToolbar>
    </>
  );
};

const BulkEnhanceReviewModal = () => {
  const { t } = useTranslation();
  const { isMobileOrTablet } = useResponsive();
  const { reviewBatch, closeReviewBatch, mergeBatch, reportBatchError } = useBulkEnhancement();
  const [applyBatchItems] = useApplyImageProcessingBatchItemsMutation();
  const [discardBatchItems] = useDiscardImageProcessingBatchItemsMutation();
  const [fetchVehicle] = useLazyGetVehicleQuery();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [runningAction, setRunningAction] = useState<ReviewAction | null>(null);
  const [originalUrlByMediaId, setOriginalUrlByMediaId] = useState<Record<number, string>>({});

  const readyItems = useMemo(
    () => (reviewBatch ? getReadyBatchItems(reviewBatch) : []),
    [reviewBatch],
  );
  const vehicleId = reviewBatch?.vehicle_id;

  useEffect(() => {
    setCurrentIndex(0);
  }, [reviewBatch?.id]);

  // The batch only carries the enhanced results, so the originals come from the vehicle gallery.
  useEffect(() => {
    if (!vehicleId) {
      return;
    }

    setOriginalUrlByMediaId({});

    void fetchVehicle(vehicleId)
      .unwrap()
      .then(({ data }) => {
        const gallery: Media[] = data?.gallery?.data || [];

        setOriginalUrlByMediaId(
          gallery.reduce<Record<number, string>>((urls, media) => {
            urls[media.id] = media.url;
            return urls;
          }, {}),
        );
      })
      .catch(() => {
        setOriginalUrlByMediaId({});
      });
  }, [fetchVehicle, vehicleId]);

  useEffect(() => {
    setCurrentIndex((index) => Math.min(index, Math.max(readyItems.length - 1, 0)));
  }, [readyItems.length]);

  if (!reviewBatch) {
    return null;
  }

  const { title, thumbnail, vin } = getVehicleDisplayInfo(reviewBatch);
  const currentItem = readyItems[currentIndex];
  const isBusy = runningAction !== null;

  const runReviewAction = async (
    action: ReviewAction,
    mutate: typeof applyBatchItems,
    selection: Pick<BatchSelectionParams, 'jobIds' | 'scope'>,
  ) => {
    setRunningAction(action);

    try {
      const { batch, results } = await mutate({
        vehicleId: reviewBatch.vehicle_id,
        batchId: reviewBatch.id,
        ...selection,
      }).unwrap();

      mergeBatch(batch);

      const failures = results.filter((result) => result.status === 'error');

      if (failures.length) {
        notifyError(t('vehicleScreen:bulk_enhance_action_error', { count: failures.length }));
      }

      if (!getReadyBatchItems(batch).length) {
        closeReviewBatch();
      }
    } catch (error) {
      reportBatchError(error);
    } finally {
      setRunningAction(null);
    }
  };

  const applyCurrent = () => {
    if (currentItem) {
      void runReviewAction('apply-item', applyBatchItems, { jobIds: [currentItem.job_id] });
    }
  };

  const discardCurrent = () => {
    if (currentItem) {
      void runReviewAction('discard-item', discardBatchItems, { jobIds: [currentItem.job_id] });
    }
  };

  const applyAll = () => {
    void runReviewAction('apply-all', applyBatchItems, {
      scope: IMAGE_PROCESSING_BATCH_ITEM_STATUS.READY,
    });
  };

  const discardAll = () => {
    void runReviewAction('discard-all', discardBatchItems, {
      scope: IMAGE_PROCESSING_BATCH_ITEM_STATUS.READY,
    });
  };

  return (
    <StyledModal
      open
      onCancel={closeReviewBatch}
      width={isMobileOrTablet ? 'calc(100vw - 32px)' : 760}
      maskClosable={!isBusy}
      closable={!isBusy}
      title={
        <HeaderModalCustom>
          <div>
            <h3>
              <WandIcon size={20} />
              {t('vehicleScreen:bulk_enhance_review_title')}
            </h3>
          </div>
        </HeaderModalCustom>
      }
      footer={[
        <Button
          key="discard-all"
          loading={runningAction === 'discard-all'}
          disabled={isBusy || !readyItems.length}
          onClick={discardAll}
        >
          {t('vehicleScreen:bulk_enhance_discard_all')}
        </Button>,
        <Button
          key="apply-all"
          type="primary"
          loading={runningAction === 'apply-all'}
          disabled={isBusy || !readyItems.length}
          onClick={applyAll}
        >
          {t('vehicleScreen:bulk_enhance_apply_all')}
        </Button>,
      ]}
    >
      <ReviewHeader>
        <VehicleThumb $url={thumbnail} />
        <ReviewMeta>
          <h4>{title}</h4>
          {vin ? <span>{vin}</span> : null}
        </ReviewMeta>
      </ReviewHeader>

      {currentItem ? (
        <ReviewItemPanel
          vehicleId={reviewBatch.vehicle_id}
          item={currentItem}
          originalUrl={originalUrlByMediaId[currentItem.media_id]}
          currentIndex={currentIndex}
          total={readyItems.length}
          runningAction={runningAction}
          onPrevious={() => setCurrentIndex((index) => Math.max(index - 1, 0))}
          onNext={() => setCurrentIndex((index) => Math.min(index + 1, readyItems.length - 1))}
          onApply={applyCurrent}
          onDiscard={discardCurrent}
        />
      ) : (
        <div>{t('vehicleScreen:bulk_enhance_review_empty')}</div>
      )}
    </StyledModal>
  );
};

export default BulkEnhanceReviewModal;
